/* Baltic Approaches — placeholder geometry.
 *
 * CON-01 demonstration: every coordinate here is inline. The prototype makes zero
 * network requests, which is the constraint the production Atlas bundle satisfies with
 * a staged PMTiles file (docs/06-data-sources/GEOSPATIAL-DATA-SOURCES.md §9).
 *
 * This is DELIBERATELY coarse hand-authored coastline, not survey data. Production
 * geometry comes from Natural Earth + OSM inside an `orbis-atlas stage` bundle.
 */
import type { FeatureCollection, Polygon, LineString } from 'geojson'

type Ring = [number, number][]

const SCANIA: Ring = [
  [12.5, 58.2], [12.0, 57.3], [12.6, 56.4], [12.85, 56.0], [12.9, 55.38],
  [13.4, 55.34], [14.35, 55.42], [14.6, 55.9], [16.0, 56.1], [16.5, 56.6],
  [17.0, 57.3], [17.6, 58.2], [12.5, 58.2],
]

const ZEALAND: Ring = [
  [11.0, 56.05], [12.0, 56.1], [12.6, 56.05], [12.68, 55.62], [12.55, 55.2],
  [12.0, 54.98], [11.2, 55.1], [10.95, 55.6], [11.0, 56.05],
]

const JUTLAND: Ring = [
  [8.1, 57.1], [9.4, 57.6], [10.55, 57.72], [10.8, 57.0], [10.6, 56.3],
  [10.3, 55.8], [9.9, 55.5], [9.45, 54.82], [8.6, 55.0], [8.15, 55.6],
  [8.1, 57.1],
]

const FUNEN: Ring = [
  [9.75, 55.6], [10.75, 55.55], [10.8, 55.0], [10.2, 54.85], [9.8, 55.1], [9.75, 55.6],
]

const GERMANY: Ring = [
  [8.0, 53.6], [8.3, 54.9], [9.45, 54.82], [10.2, 54.45], [11.3, 54.42],
  [12.4, 54.48], [13.4, 54.1], [14.28, 53.92], [14.3, 53.4], [8.0, 53.4],
  [8.0, 53.6],
]

const POLAND: Ring = [
  [14.28, 53.92], [15.2, 54.15], [16.5, 54.55], [17.6, 54.8], [18.35, 54.82],
  [19.0, 54.42], [19.65, 54.45], [19.65, 53.4], [14.3, 53.4], [14.28, 53.92],
]

const BORNHOLM: Ring = [
  [14.68, 55.3], [15.16, 55.28], [15.14, 54.99], [14.7, 55.0], [14.68, 55.3],
]

const GOTLAND: Ring = [
  [18.1, 57.95], [19.0, 57.85], [18.85, 57.1], [18.15, 56.9], [17.95, 57.4], [18.1, 57.95],
]

const LAND_RINGS: Ring[] = [
  SCANIA, ZEALAND, JUTLAND, FUNEN, GERMANY, POLAND, BORNHOLM, GOTLAND,
]

export const land: FeatureCollection<Polygon> = {
  type: 'FeatureCollection',
  features: LAND_RINGS.map((ring, i) => ({
    type: 'Feature',
    id: i,
    properties: {},
    geometry: { type: 'Polygon', coordinates: [ring] },
  })),
}

/** Graticule at 1° intervals over the AOI. Generated, not stored. */
function buildGraticule(): FeatureCollection<LineString> {
  const [w, s, e, n] = [7, 53, 21, 59]
  const features: FeatureCollection<LineString>['features'] = []

  for (let lon = w; lon <= e; lon += 1) {
    features.push({
      type: 'Feature',
      properties: { major: lon % 5 === 0 },
      geometry: {
        type: 'LineString',
        coordinates: [[lon, s], [lon, n]],
      },
    })
  }
  for (let lat = s; lat <= n; lat += 1) {
    features.push({
      type: 'Feature',
      properties: { major: lat % 5 === 0 },
      geometry: {
        type: 'LineString',
        coordinates: [[w, lat], [e, lat]],
      },
    })
  }
  return { type: 'FeatureCollection', features }
}

export const graticule = buildGraticule()

/** Depth contours — placeholder for staged GEBCO/EMODnet bathymetry. */
export const bathymetry: FeatureCollection<LineString> = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: { depth: 50 },
      geometry: {
        type: 'LineString',
        coordinates: [
          [12.2, 56.4], [13.4, 55.9], [14.6, 55.7], [15.9, 55.6], [17.2, 55.9],
          [18.3, 56.5], [18.6, 57.2],
        ],
      },
    },
    {
      type: 'Feature',
      properties: { depth: 100 },
      geometry: {
        type: 'LineString',
        coordinates: [
          [13.0, 55.1], [14.4, 54.9], [15.8, 54.85], [17.1, 55.05], [18.2, 55.6],
          [18.9, 56.4],
        ],
      },
    },
  ],
}

export const AOI = {
  name: 'Baltic Approaches',
  center: [14.6, 55.6] as [number, number],
  zoom: 5.9,
  bounds: [[7, 53], [21, 59]] as [[number, number], [number, number]],
}
